import { useRouter } from "next/router";
import { QUERY_PARAM_KEY } from "utils/constants/query";
import {
  getAdminContext,
  getApplicationTheme,
  getCapabilities,
  getCtxAppResourceAndIDs,
  getCtxModelsForResourceAndUUID,
  inferModelsForResource,
  orderedApplications
} from "utils/mapStateToProps";
import { MODEL_RESOURCES, USER } from "utils/constants/models";
import { useSelector } from "react-redux";
import { getEnvCollection } from "components/FormerEditor/common";
import { useApplicationContext } from "./context";
import { SHORT_LINK_ACTION_UID } from "./constants/shortLink";
import { STATE_KEYS } from "./constants/state";
import get from "lodash/get";
import { EVENT_NAME } from "./constants/event";
import { getJWT } from "./auth";

/**
 * We set the env to be test when in marketing builder mode
 * - This allows us to filter by resources where !livemode
 * - Else we default te the NODE_ENV so that
 * -- development still works (i.e. getEnvCollection filters where NODE_ENV !== 'production')
 * -- vica versa -> prodution works
 */
export const useCtxEnv = () => {
  const appCtx = useApplicationContext();

  return appCtx ? appCtx.env : process.env.NODE_ENV;
};

/**
 * Application context is what most navigation is derived from. Reference from
 * - context
 * - query string
 * - store
 * @returns {Object<{
 * application: Object,
 * resourceId: String,
 * resourceUUID: String,
 * resource: String,
 * merchantAccount: Object,
 * merchantAccountUUID: Object,
 * customerId: String,
 * capability: Object,
 * counts: Object,
 * enabled: Object}>}} result
 */
export const useCtxReferences = () => {
  const router = useRouter();
  const appCtx = useApplicationContext();
  /**
   * Applications are ordered by resource priority
   * 0. Org apps
   * 1. User apps
   */
  const applications = useOrderedApplications();
  const paramAppUUID = router.query[QUERY_PARAM_KEY.APPLICATION_UUID];

  let applicationUUID;
  if (appCtx && appCtx.application.uuid) {
    applicationUUID = appCtx.application.uuid;
  } else if (paramAppUUID) {
    applicationUUID = paramAppUUID;
  } else if (applications && applications.length) {
    applicationUUID = applications[0].uuid;
  }

  return useSelector((state) =>
    getCtxAppResourceAndIDs(state, applicationUUID)
  );
};

export const useViewsForSubdomainKey = ({ subdomain, key }) => {
  /**
   * Computations are nested under a subdomain[key]
   */
  const computations = useSelector((state) =>
    state.getIn(["computation", "data"])
  ).toJS();

  return computations[subdomain] && computations[subdomain][key]
    ? computations[subdomain][key]
    : [];
};

export const useShortLink = (id, key = "id") =>
  useSelector((state) => {
    const match = state
      .getIn(["shortLink", "data"])
      .find((shortLink) => shortLink.get(key) === id);
    return match ? match.toJS() : null;
  });

export const useShortLinks = () =>
  useSelector((state) => state.getIn(["shortLink", "data"]).toJS());

export const useSubscriptions = () =>
  useSelector((state) => state.getIn(["subscription", "data"]).toJS());

export const useApplications = () =>
  useSelector((state) => state.getIn(["application", "data"]).toJS());

export const useReducerLoading = (reducer) =>
  useSelector((state) => Boolean(state.getIn([reducer, "isLoading"])));

export const useVariables = () =>
  useSelector((state) => state.getIn(["variable", "data"]).toJS());

export const useCoupon = (id, key = "id") =>
  useSelector((state) => {
    const match = state
      .getIn(["coupon", "data"])
      .find((coupon) => coupon.get(key) === id);
    return match ? match.toJS() : null;
  });

export const useEvents = (name, key = "name") =>
  useSelector((state) => {
    const events = state.getIn(["event", "data"]);

    return name
      ? events.filter((event) => event.get(key) === name).toJS()
      : events.toJS();
  });

export const useAssocMetadata = ({ shortId, applicationId }) =>
  useSelector((state) => {
    const metadatas = state.getIn(["metadata", "data"]);
    let match;
    if (shortId) {
      match = metadatas.find(
        (metadata) => metadata.get("short_id") === shortId
      );
    } else if (applicationId) {
      match = metadatas.find(
        (metadata) => metadata.get("application_id") === applicationId
      );
    }

    return match ? match.toJS() : null;
  });

export const useAppEntities = () =>
  useSelector((state) => state.getIn(["app", "data", "entities"]).toJS());

export const useAppBuilder = () =>
  useSelector((state) => state.getIn(["app", "data", "builder"]).toJS());

export const useApplicationTheme = () => {
  const router = useRouter();
  const appCtx = useApplicationContext();
  const applicationUUID = appCtx
    ? appCtx.application.uuid
    : router.query[QUERY_PARAM_KEY.APPLICATION_UUID];

  return useSelector((state) =>
    getApplicationTheme(state, { applicationUUID })
  );
};

export const useAppAdmin = (uuid) =>
  useSelector((state) =>
    getAdminContext(state, {
      applicationUUID: uuid
    })
  );

export const useSale = (id, key = "id") =>
  useSelector((state) => {
    const saleMatch = state
      .getIn(["event", "data"])
      .find(
        (event) =>
          event.get(key) === id &&
          event.get("name") === EVENT_NAME.MERCHANT_ACCOUNT_CHECKOUT_COMPLETED
      );
    return saleMatch ? saleMatch.toJS() : null;
  });

export const usePayment = (id) =>
  useSelector((state) => {
    const modelMatch = state
      .getIn(["payment", "data"])
      .find((event) => event.get("id") === id);
    return modelMatch ? modelMatch.toJS() : null;
  });

export const useSession = (id) =>
  useSelector((state) => {
    const modelMatch = state
      .getIn(["session", "data"])
      .find((event) => event.get("id") === id);
    return modelMatch ? modelMatch.toJS() : null;
  });

export const useProduct = (id) => {
  const env = useCtxEnv();
  return useSelector((state) => {
    const products = getEnvCollection(
      state.getIn(["product", "data"]).toJS(),
      env
    );
    const prices = getEnvCollection(state.getIn(["price", "data"]).toJS(), env);
    const productMatch = products.find((product) => product.id === id);
    const productPrices = productMatch
      ? prices.filter((price) => price.product === productMatch.id)
      : [];

    return productMatch
      ? {
          ...productMatch,
          prices: productPrices
        }
      : null;
  });
};

export const useMerchantAccount = ({ uuid }) => {
  const merchantAccount = useSelector((state) =>
    state
      .getIn(["merchantAccount", "data"])
      .find((merchantAccount) => merchantAccount.get("uuid") === uuid)
  );

  return merchantAccount && merchantAccount.toJS();
};

export const useManifest = (id, key = "uuid") => {
  const model = useSelector((state) =>
    state
      .getIn(["manifest", "data"])
      .find((manifest) => manifest.get(key) === id)
  );

  return model && model.toJS();
};

export const useFeatureGroups = () => {
  const { resource } = useCtxReferences();

  return useSelector((state) => {
    return getCtxModelsForResourceAndUUID(
      state,
      MODEL_RESOURCES.FEATURE_GROUP,
      resource
    );
  });
};

export const useFeatureGroupFeatures = () => {
  const { resource } = useCtxReferences();

  return useSelector((state) => ({
    features: getCtxModelsForResourceAndUUID(
      state,
      MODEL_RESOURCES.FEATURE,
      resource
    ),
    featureGroups: getCtxModelsForResourceAndUUID(
      state,
      MODEL_RESOURCES.FEATURE_GROUP,
      resource
    )
  }));
};

export const useCheckoutTracking = () => {
  const router = useRouter();

  const appUUID =
    router.query.application || router.query[QUERY_PARAM_KEY.APPLICATION_UUID];
  const pageUUID = router.query.page || router.query[QUERY_PARAM_KEY.PAGE_UUID];

  const result = useSelector((state) => {
    const result = {};

    const manifest = state
      .getIn(["manifest", "data"])
      .find((model) => model.get("uuid") === pageUUID);
    if (manifest) {
      result.manifest_id = manifest.get("id");
    }
    const application = state
      .getIn(["application", "data"])
      .find((model) => model.get("uuid") === appUUID);
    const userUUID = state.getIn(["user", "data", "uuid"]);
    if (userUUID) {
      result.user_uuid = userUUID;
    }
    if (application) {
      result.subdomain = application.get("subdomain");
    }

    return result;
  });

  return result;
};

export const useMerchantAccounts = () =>
  useSelector((state) => state.getIn(["merchantAccount", "data"]).toJS());

export const useUserMerchantAccounts = () =>
  useSelector((state) => state.getIn(["userMerchantAccount", "data"]).toJS());

export const useOrgMerchantAccounts = () =>
  useSelector((state) =>
    state.getIn(["organizationMerchantAccount", "data"]).toJS()
  );

export const useResourceApiKeys = () => {
  const { resource, resourceUUID } = useCtxReferences();
  if (resource === USER) {
    return useSelector((state) =>
      state.getIn(["user", "data", "api_keys"]).toJS()
    );
  } else {
    return useSelector((state) => {
      const org = state
        .getIn(["organization", "data"])
        .find((model) => model.get("uuid") === resourceUUID);
      if (org && org.get("api_keys")) {
        return org.get("api_keys").toJS();
      } else {
        return [];
      }
    });
  }
};

export const useInferModelsForResource = (modelResource) =>
  useSelector((state) => inferModelsForResource(state, modelResource));

export const useUserApiKeys = () =>
  useSelector((state) => state.getIn(["user", "data", "api_keys"]).toJS());

export const useOrgApiKeys = () =>
  useSelector((state) => state.getIn(["user", "data", "api_keys"]).toJS());

export const useProductPrices = (uid) => {
  const env = useCtxEnv();
  return useSelector((state) => {
    return getEnvCollection(state.getIn(["price", "data"]).toJS(), env).filter(
      (price) => price.product === uid
    );
  });
};

export const useProductsPrices = () => {
  const env = useCtxEnv();
  return useSelector((state) => ({
    products: getEnvCollection(state.getIn(["product", "data"]).toJS(), env),
    prices: getEnvCollection(state.getIn(["price", "data"]).toJS(), env)
  }));
};

export const useCouponPromotionCodes = () => {
  const env = useCtxEnv();
  return useSelector((state) => ({
    coupons: getEnvCollection(state.getIn(["coupon", "data"]).toJS(), env),
    promotionCodes: getEnvCollection(
      state.getIn(["promotionCode", "data"]).toJS(),
      env
    )
  }));
};

export const useTaxRates = () => {
  const env = useCtxEnv();
  return useSelector((state) =>
    getEnvCollection(state.getIn(["taxRate", "data"]).toJS(), env)
  );
};

export const useTaxRate = (id, key = "id") =>
  useSelector((state) => {
    const match = state
      .getIn(["taxRate", "data"])
      .find((taxRate) => taxRate.get(key) === id);
    return match ? match.toJS() : null;
  });

export const useAppShortlinks = () => {
  const { resource } = useCtxReferences();

  return useSelector((state) => {
    return getCtxModelsForResourceAndUUID(
      state,
      MODEL_RESOURCES.SHORT_LINK,
      resource
    );
  });
};

export const useAttachments = () =>
  useSelector((state) => state.getIn(["attachment", "data"]).toJS());

export const useAttachment = (id, key = "id") =>
  useSelector((state) => {
    const match = state
      .getIn(["attachment", "data"])
      .find((attachment) => attachment.get(key) === id);
    return match ? match.toJS() : null;
  });

export const useCampaigns = () =>
  useSelector((state) => state.getIn(["campaign", "data"]).toJS());

export const useCampaign = (id, key = "id") =>
  useSelector((state) => {
    const match = state
      .getIn(["campaign", "data"])
      .find((campaign) => campaign.get(key) === id);
    return match ? match.toJS() : null;
  });

export const useDatasources = () =>
  useSelector((state) => state.getIn(["datasource", "data"]).toJS());

export const useDatasource = (id, key = "id") =>
  useSelector((state) => {
    const match = state
      .getIn(["datasource", "data"])
      .find((datasource) => datasource.get(key) === id);
    return match ? match.toJS() : null;
  });

export const useModelReport = (model, id, key = "id") =>
  useSelector((state) => {
    const match = state
      .getIn(["report", "data"])
      .find(
        (report) => report.get("model") === model && report.get(key) === id
      );
    return match ? match.toJS() : null;
  });

export const useAudiences = () =>
  useSelector((state) => state.getIn(["audience", "data"]).toJS());

export const useAudience = (id, key = "id") =>
  useSelector((state) => {
    const match = state
      .getIn(["audience", "data"])
      .find((audience) => audience.get(key) === id);
    return match ? match.toJS() : null;
  });

const getSelections = (selections, models) =>
  selections && Array.isArray(selections)
    ? selections.reduce((memo, code) => {
        const modelMatch = models.find((model) => model.code === code);

        if (modelMatch) {
          memo.push({
            name: modelMatch.name,
            id: modelMatch.code
          });
        }
        return memo;
      }, [])
    : [];

/**
 *
 * @param {Array} selections - collection of country codes (e.g. US, IE etc.)
 */
export const useCountrySelections = (selections = []) => {
  const countriesState = useSelector((state) =>
    state.getIn(["app", "data", "checkout", "countries"])
  );
  const countries = countriesState ? countriesState.toJS() : [];
  let options = [];
  let countrySelections = [];
  if (countries.length > 0) {
    options = countries.map(({ name, code }) => ({
      name,
      id: code
    }));
    countrySelections = getSelections(selections, countries);
  }

  return {
    options,
    selections: countrySelections
  };
};

// export const usePaymentMethodSelections = (selections = []) => {
//   const { merchantAccount } = useCtxReferences();
//   const account = get(merchantAccount, "account");

//   const paymentMethodsState = useSelector((state) =>
//     state.getIn(["app", "data", "checkout", "paymentMethods"])
//   );
//   let options = [];
//   if (!isEmpty(account) && paymentMethodsState) {
//     options = getPaymentMethodOptions({
//       account,
//       options: paymentMethodsState.toJS()
//     });
//   }

//   return {
//     options,
//     selections: selections.reduce((memo, paymentMethod) => {
//       const optionMatch = options.find(({ id }) => id === paymentMethod);
//       if (optionMatch) {
//         memo.push(optionMatch);
//       }
//       return memo;
//     }, [])
//   };
// };

export const useStateSelections = (selections = []) => {
  const statesState = useSelector((state) =>
    state.getIn(["app", "data", "checkout", "states"])
  );
  const states = statesState ? statesState.toJS() : [];
  let options = [];
  let stateSelections = [];
  if (states.length > 0) {
    options = states.map(({ name, code }) => ({
      name,
      id: code
    }));
    stateSelections = getSelections(selections, states);
  }

  return {
    options,
    selections: stateSelections
  };
};

export const useIntegrations = () =>
  useSelector((state) => state.getIn(["integration", "data"]).toJS());

export const useIntegrationForService = (service) =>
  useSelector((state) => {
    const integrationMatch = state
      .getIn(["integration", "data"])
      .find((integration) => integration.get("service") === service);
    return integrationMatch ? integrationMatch.toJS() : null;
  });

export const useProductPaymentLinks = (uid) => {
  const shortLinks = useAppShortlinks();

  return shortLinks.reduce((memo, shortLink) => {
    if (shortLink.data.action_uid === SHORT_LINK_ACTION_UID.MERCHANT_CHECKOUT) {
      const primaries = get(shortLink, STATE_KEYS.SHORT_LINK.PRIMARIES, []);
      const productMatch = primaries.find(({ product }) => product === uid);
      if (productMatch) {
        memo.push(shortLink);
      }
    } else {
      throw new Error(`Unsupport action: ${shortLink.data.action_uid}`);
    }
    return memo;
  }, []);
};

export const useOrderedApplications = () =>
  useSelector((state) => orderedApplications(state));

export const useAuthLoading = () =>
  useSelector((state) => Boolean(state.getIn(["auth", "isLoading"])));

export const useUser = () =>
  useSelector((state) => state.getIn(["user", "data"]).toJS());

export const useCapabilities = () =>
  useSelector((state) => getCapabilities(state));

export const useIsAuthed = () =>
  useSelector((state) => {
    const userId = state.getIn(["user", "data", "id"]);
    return Boolean(userId && getJWT());
  });

export const useForceFetchShortLinkResources = (shortLink) => {
  const {
    products: currentProducts,
    prices: currentPrices
  } = useProductsPrices();

  const primaries = get(shortLink, STATE_KEYS.SHORT_LINK.PRIMARIES, []);
  const missingResources = primaries.reduce((memo, primary) => {
    const productMatch = currentProducts.find(
      ({ id }) => id === primary.product
    );
    const priceMatch = currentPrices.find(({ id }) => id === primary.price);

    if (!productMatch || !priceMatch) {
      memo.push(primary);
    }

    return memo;
  }, []);

  return missingResources.length > 0;
};
